import React, { useCallback, useEffect, useMemo, useState } from 'react';
import projectsData from '../config/projects';

const stripHtml = (html) => (html ? html.replace(/<[^>]*>/g, '') : '');
const isExternal = (url) => !!url && /^(https?:)?\/\//.test(url);

// Fixed filter button order. 'All' is always first; the four engineering
// categories follow in the user-specified sequence. Categories with zero
// matching projects are dropped automatically.
const FILTER_ORDER = [
  'All',
  'Mechanical Engineering',
  'Electrical Engineering',
  'Computer Engineering',
  'Embedded Systems',
];

const githubIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
  </svg>
);

const playIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M8 5v14l11-7L8 5z" />
  </svg>
);

const ProjectsSection = () => {
  // Sort by priorityIndex ascending; missing priorities sink to the bottom
  // (and break ties with id descending so newer ones float).
  const sortedAll = useMemo(() => {
    return [...projectsData].sort((a, b) => {
      const ap = a.priorityIndex ?? Number.POSITIVE_INFINITY;
      const bp = b.priorityIndex ?? Number.POSITIVE_INFINITY;
      if (ap !== bp) return ap - bp;
      return (b.id || 0) - (a.id || 0);
    });
  }, []);

  // Filter buttons appear in FILTER_ORDER. A category is shown only if at
  // least one project carries that tag.
  const allTags = useMemo(() => {
    const present = new Set();
    sortedAll.forEach((p) => (p.tag || []).forEach((t) => present.add(t)));
    return FILTER_ORDER.filter((t) => t === 'All' || present.has(t));
  }, [sortedAll]);

  const [tag, setTag] = useState('All');

  const handleTagChange = useCallback((newTag) => setTag(newTag), []);

  const filtered = useMemo(() => {
    if (tag === 'All') return sortedAll;
    return sortedAll.filter((p) => (p.tag || []).includes(tag));
  }, [tag, sortedAll]);

  // Re-arm scroll-reveal whenever the filtered list changes.
  useEffect(() => {
    const reveals = document.querySelectorAll(
      '.reveal, .reveal-left, .reveal-right, .reveal-scale'
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('reveal--visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { root: null, rootMargin: '0px', threshold: 0.15 }
    );
    reveals.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [filtered]);

  return (
    <section className="projects projects-flow" id="projects-flow">
      <h2 className="projects__heading section-heading reveal">Projects</h2>

      <div className="publications__filters reveal">
        {allTags.map((t) => (
          <button
            key={t}
            type="button"
            className={`publication__filter-btn${
              tag === t ? ' publication__filter-btn--active' : ''
            }`}
            onClick={() => handleTagChange(t)}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="publications__container">
        {filtered.map((project, i) => {
          const isLeft = i % 2 === 0;
          const positionClass = isLeft ? 'project-left' : 'project-right';
          const revealDir = isLeft ? 'reveal-left' : 'reveal-right';

          const containerClasses = [
            'project__image-container',
            isLeft ? 'weather' : 'todo',
            project.imageBgWhite ? 'project__image-container--white-bg' : '',
          ]
            .filter(Boolean)
            .join(' ');

          const imageClasses = [
            'project__image-image',
            project.imageContain ? 'project__image-image--contain' : '',
          ]
            .filter(Boolean)
            .join(' ');

          const altText = stripHtml(project.title);

          // Link target priority: liveUrl > detailUrl > /project?id=X (if detail) > gitUrl
          const detailHref =
            project.detailUrl ||
            (project.detail ? `/project?id=${project.id}` : null);
          const primaryUrl =
            project.liveUrl ||
            detailHref ||
            (project.gitUrl && project.gitUrl !== '/' ? project.gitUrl : null);
          const externalAttrs = (url) =>
            isExternal(url) ? { target: '_blank', rel: 'noopener noreferrer' } : {};

          const imageInner = (
            <>
              <div className={imageClasses}>
                <img
                  src={project.image}
                  alt={altText}
                  loading={i > 0 ? 'lazy' : undefined}
                  decoding="async"
                />
              </div>
              {project.overlayImage && (
                <img
                  className="project__image-overlay"
                  src={project.overlayImage}
                  alt={project.overlayCaption || ''}
                  loading={i > 0 ? 'lazy' : undefined}
                  decoding="async"
                />
              )}
            </>
          );

          // Real links (crawlable, middle-clickable) instead of div role=link.
          const imageContainer = primaryUrl ? (
            <a
              className={containerClasses}
              href={primaryUrl}
              aria-label={altText}
              style={{ position: 'relative', display: 'block' }}
              {...externalAttrs(primaryUrl)}
            >
              {imageInner}
            </a>
          ) : (
            <div className={containerClasses} style={{ position: 'relative' }}>
              {imageInner}
            </div>
          );

          const projectInfo = (
            <div className="project__info">
              {project.course && (
                <div className="project__course">{project.course}</div>
              )}
              <h3 className="project__title">
                {primaryUrl ? (
                  <a
                    href={primaryUrl}
                    {...externalAttrs(primaryUrl)}
                    dangerouslySetInnerHTML={{ __html: project.title }}
                  />
                ) : (
                  <span dangerouslySetInnerHTML={{ __html: project.title }} />
                )}
              </h3>
              <p
                className="project__description"
                dangerouslySetInnerHTML={{ __html: project.description }}
              />
              <div className="project__actions">
                {project.liveUrl && (
                  <button
                    type="button"
                    className="project__preview-btn project__preview-btn--play"
                    onClick={() =>
                      window.open(project.liveUrl, '_blank', 'noopener,noreferrer')
                    }
                    aria-label="Play live demo"
                    title="Play live demo"
                  >
                    {playIcon}
                  </button>
                )}
                {detailHref && (
                  <a
                    className="project__details-link"
                    href={detailHref}
                    {...externalAttrs(detailHref)}
                  >
                    Details &rarr;
                  </a>
                )}
                {project.gitUrl && project.gitUrl !== '/' && (
                  <a
                    href={project.gitUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="project__preview-btn project__preview-btn--git"
                    aria-label="View on GitHub"
                    title="View on GitHub"
                  >
                    {githubIcon}
                  </a>
                )}
              </div>
            </div>
          );

          return (
            <div
              key={project.id ?? i}
              className={`project ${positionClass} ${revealDir}`}
              data-tag={(project.tag || []).join('|')}
            >
              {isLeft ? (
                <>
                  {imageContainer}
                  {projectInfo}
                </>
              ) : (
                <>
                  {projectInfo}
                  {imageContainer}
                </>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && (
          <p className="projects-flow__empty">No projects match this filter.</p>
        )}
      </div>
    </section>
  );
};

export default ProjectsSection;
